import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { usePageMeta } from '../hooks/usePageMeta';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { FileText, Home, Building2, HeartHandshake, ArrowRight, CheckCircle2 } from 'lucide-react';

export const HomePage = () => {
  const { t } = useLanguage();
  usePageMeta('seo.homeTitle', 'seo.homeDescription');

  const services = [
    {
      icon: FileText,
      title: t('home.service1Title'),
      description: t('home.service1Desc'),
      link: '/services/private',
    },
    {
      icon: Home,
      title: t('home.service2Title'),
      description: t('home.service2Desc'),
      link: '/services/private',
    },
    {
      icon: Building2,
      title: t('home.service3Title'),
      description: t('home.service3Desc'),
      link: '/services/private',
    },
    {
      icon: HeartHandshake,
      title: t('home.service4Title'),
      description: t('home.service4Desc'),
      link: '/services/private',
    },
  ];

  const whyChooseUs = [
    { text: t('home.whyPoint1') },
    { text: t('home.whyPoint2') },
    { text: t('home.whyPoint3') },
    { text: t('home.whyPoint4') },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-sea-blue/5 via-white to-bronze/5 py-20 md:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                {t('hero.title')}
              </h1>
              <p className="text-lg md:text-xl text-gray-600">
                {t('hero.subtitle')}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/booking">
                  <Button size="lg" className="bg-sea-blue hover:bg-sea-blue/90 text-white" data-testid="hero-book-btn">
                    {t('hero.bookCta')}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button size="lg" variant="outline" className="border-sea-blue text-sea-blue hover:bg-sea-blue/5">
                    {t('hero.learnMore')}
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_9c9fe7ed-0dc2-44e3-88b0-3bb9e445fe22/artifacts/nj6v9hep_WhatsApp%20Image%202026-02-18%20at%2020.54.22.jpeg"
                  alt="Copenhagen Denmark"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-bronze text-white p-6 rounded-lg shadow-lg max-w-xs">
                <p className="font-semibold text-lg">{t('hero.badge')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Next Step Denmark Section */}
      <section className="py-20 bg-gradient-to-br from-sea-blue/5 via-white to-bronze/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {t('homeAbout.title')}
            </h2>
            <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
              <p>{t('homeAbout.p1')}</p>
              <p>{t('homeAbout.p2')}</p>
              <p>{t('homeAbout.p3')}</p>
              <div className="pt-4">
                <Link to="/about">
                  <Button 
                    variant="outline" 
                    className="border-sea-blue text-sea-blue hover:bg-sea-blue hover:text-white"
                  >
                    {t('homeAbout.readMore')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('home.servicesTitle')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('home.servicesSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Link
                  key={index}
                  to={service.link}
                  className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-sea-blue focus-visible:ring-offset-2 rounded-lg"
                  data-testid={`home-service-card-${index + 1}`}
                  aria-label={service.title}
                >
                  <Card className="h-full hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border-2 group-hover:border-sea-blue/40 cursor-pointer">
                    <CardContent className="p-6 flex flex-col h-full space-y-4">
                      <div className="w-14 h-14 rounded-lg bg-sea-blue/10 flex items-center justify-center group-hover:bg-sea-blue group-hover:scale-110 transition-all duration-300">
                        <Icon className="h-7 w-7 text-sea-blue group-hover:text-white transition-colors" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed flex-grow">
                        {service.description}
                      </p>
                      <span className="inline-flex items-center gap-1 text-sea-blue font-semibold text-sm pt-2 group-hover:gap-2 transition-all">
                        {t('hero.learnMore')}
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/services/private">
              <Button size="lg" variant="outline" className="border-sea-blue text-sea-blue hover:bg-sea-blue hover:text-white" data-testid="home-private-cta">
                {t('home.viewPrivateServices')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/services/companies">
              <Button size="lg" variant="outline" className="border-bronze text-bronze hover:bg-bronze hover:text-white" data-testid="home-companies-cta">
                {t('home.viewCompanyServices')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-br from-bronze/5 via-white to-sea-blue/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_9c9fe7ed-0dc2-44e3-88b0-3bb9e445fe22/artifacts/uukmjsae_ChatGPT%20Image%2022.%20feb.%202026%2C%2013.48.14.png"
                  alt="New Beginning"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="space-y-8">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  {t('home.whyUsTitle')}
                </h2>
                <p className="text-lg text-gray-600">
                  {t('home.whyUsSubtitle')}
                </p>
              </div>

              <div className="space-y-4">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-sea-blue flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">{item.text}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <Link to="/about">
                  <Button size="lg" className="bg-bronze hover:bg-bronze/90 text-white">
                    {t('home.learnMoreAbout')}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-sea-blue text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t('home.ctaTitle')}
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            {t('home.ctaSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/booking">
              <Button size="lg" className="bg-bronze hover:bg-bronze/90 text-white">
                {t('home.ctaBookButton')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-sea-blue">
                {t('home.ctaButton')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
